import type { HaloSite, HaloUser } from '../types/halo';

// Contact matching (design doc § Contact Matching). Halo stores phone numbers as free text in
// mixed formats and ?search= is a raw substring match, so we search by the last 4 digits (the only
// query that hits every stored format) and then compare normalized numbers here.

/** Trailing extension: "x123", "ext. 12", "extension 4", "#5". */
const EXTENSION = /\s*(?:x|ext\.?|extension|#)\s*\d+\s*$/i;

/**
 * The comparison key for a phone number: its last 10 digits. Null for placeholders ("-"),
 * blanks and anything too short to be a full number.
 */
export function phoneKey(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(EXTENSION, '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : null;
}

/** "(780) 555-1234" for a North American number, otherwise the input as given. */
export function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const nanp = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return nanp.length === 10 ? `(${nanp.slice(0, 3)}) ${nanp.slice(3, 6)}-${nanp.slice(6)}` : raw;
}

/** Why a contact is in the list. Lower rank = stronger. */
export type MatchKind = 'remembered' | 'email' | 'direct' | 'site-phone' | 'site' | 'search';

const RANK: Record<MatchKind, number> = { remembered: 0, email: 1, direct: 2, 'site-phone': 3, site: 4, search: 5 };

export interface ContactMatch {
  user: HaloUser;
  kind: MatchKind;
}

/** The user's own numbers. `sitephonenumber` is the site's number copied onto the user. */
export const DIRECT_FIELDS = ['phonenumber_preferred', 'phonenumber', 'mobilenumber', 'mobilenumber2', 'homenumber'] as const;

export function matchUsersByPhone(users: HaloUser[], key: string): ContactMatch[] {
  const out: ContactMatch[] = [];
  for (const user of users) {
    if (DIRECT_FIELDS.some((f) => phoneKey(user[f]) === key)) out.push({ user, kind: 'direct' });
    else if (phoneKey(user.sitephonenumber) === key) out.push({ user, kind: 'site-phone' });
  }
  return out;
}

export function matchSitesByPhone(sites: HaloSite[], key: string): HaloSite[] {
  return sites.filter((s) => phoneKey(s.phonenumber) === key);
}

/**
 * One entry per user, keeping the strongest reason, ordered: remembered pick, then by match kind,
 * active before inactive, important contacts first, then by name.
 */
export function rankMatches(matches: ContactMatch[], rememberedUserId?: number | null): ContactMatch[] {
  const best = new Map<number, ContactMatch>();
  for (const m of matches) {
    const kind: MatchKind = m.user.id === rememberedUserId ? 'remembered' : m.kind;
    const current = best.get(m.user.id);
    if (!current || RANK[kind] < RANK[current.kind]) best.set(m.user.id, { user: m.user, kind });
  }
  return [...best.values()].sort(
    (a, b) =>
      RANK[a.kind] - RANK[b.kind] ||
      Number(!!a.user.inactive) - Number(!!b.user.inactive) ||
      Number(!!b.user.isimportantcontact) - Number(!!a.user.isimportantcontact) ||
      a.user.name.localeCompare(b.user.name),
  );
}

/**
 * The match to select without asking: the agent's previous pick for this number, or the only
 * active contact matched by email or by their own number. Null means "let the agent pick".
 */
export function strongMatch(ranked: ContactMatch[]): ContactMatch | null {
  const first = ranked[0];
  if (first?.kind === 'remembered') return first;
  const strong = ranked.filter((m) => (m.kind === 'email' || m.kind === 'direct') && !m.user.inactive);
  return strong.length === 1 ? strong[0]! : null;
}

export interface SiteGroup {
  key: string;
  label: string;
  matches: ContactMatch[];
}

/** Groups ranked matches by client/site for the picker, keeping the rank order inside and across groups. */
export function groupBySite(ranked: ContactMatch[]): SiteGroup[] {
  const groups = new Map<string, SiteGroup>();
  for (const m of ranked) {
    const key = `${m.user.client_id ?? ''}/${m.user.site_id ?? ''}`;
    const label = [m.user.client_name, m.user.site_name].filter(Boolean).join(' · ') || 'No site';
    const g = groups.get(key) ?? { key, label, matches: [] };
    g.matches.push(m);
    groups.set(key, g);
  }
  return [...groups.values()];
}

/** Case-insensitive filter over name, email, client and site. */
export function filterMatches(matches: ContactMatch[], query: string): ContactMatch[] {
  const q = query.trim().toLowerCase();
  if (!q) return matches;
  return matches.filter(({ user: u }) => [u.name, u.emailaddress, u.client_name, u.site_name].some((v) => v?.toLowerCase().includes(q)));
}
